import { useCallback, useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import TimeAgo from 'react-timeago'
import { ChevronDown, HatGlasses } from 'lucide-react'
import { Badge, Menu, Tooltip } from '@highlight/ui'
import {
  useIsMiniHovered,
  useIsMiniContentHovered,
  useIsMiniMenuOpen,
  useSetMiniHovered,
  useSetMiniContentHovered,
  useSetMiniOpenSurface,
  useIsMiniNotched,
} from '../../../atoms'
import { useIsMiniAnchoredLeft } from '../side-context'
import { MINI_REVEAL_TRANSITION, miniPresenceAnchor, miniRevealVariants } from '../side-motion'
import { useIsLoggedIn } from '@renderer/hooks/useIsLoggedIn'
import { useIncognitoMode } from '@renderer/hooks/useIncognitoMode'
import { INCOGNITO_EXPLAINER, durationUntilMorning } from '@shared/utils/incognito'
import { noteNoun } from '@shared/utils/audio-notes/AudioNoteUtils'
import { incognitoTimeRemainingCompactFormatter } from '@renderer/utils/incognito-time-remaining'
import {
  useAudioNoteHasMeeting,
  useIsAudioNotePaused,
  useIsRecordingAudioNote,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { useOpenViewInSidepanel } from '@renderer/overlay/hooks/use-open-view-in-sidepanel'
import { useLiveMeetingIdentity } from '@renderer/overlay/Sidepanel/components/meeting/use-live-meeting-identity'
import { MEETING_VIEW_ID } from '@renderer/overlay/Sidepanel/components/meeting/meeting-view-id'
import { useSidepanelState } from '@renderer/overlay/atoms'
import { useViewNavigation } from '@renderer/overlay/Sidepanel/view-atoms'
import { viewContextOf } from '@renderer/overlay/Sidepanel/view-definitions'
import WindowIpcRenderer from '@renderer/ipc/WindowIpc.renderer'
import { twMerge } from 'tailwind-merge'
import { useStartMiniMeeting } from './use-start-mini-meeting'
import { trackNotchAction } from '../../../track-notch'

const MINI_MENU_SURFACE_ID = 'mini-menu'

export function MiniMenuButton() {
  const isNotched = useIsMiniNotched()
  const setOpenSurface = useSetMiniOpenSurface()
  const [open, setOpen] = useState(false)
  const isMiniOnLeftSide = useIsMiniAnchoredLeft()
  const setMiniHovered = useSetMiniHovered()
  const setContentHovered = useSetMiniContentHovered()
  const handleNewMeeting = useStartMiniMeeting()
  const { isIncognitoEnabled, enableIncognito, disableIncognito, incognitoTimer } = useIncognitoMode()
  const isRecording = useIsRecordingAudioNote()
  const isPaused = useIsAudioNotePaused()
  const hasMeeting = useAudioNoteHasMeeting()
  const hasActiveMeeting = isRecording || isPaused
  const { openViewInSidepanel } = useOpenViewInSidepanel()
  const liveMeeting = useLiveMeetingIdentity()
  const isLoggedIn = useIsLoggedIn()
  const sidepanelState = useSidepanelState()
  const { topView } = useViewNavigation()
  const isAlreadyInNewChat = !!viewContextOf(topView, 'chat')?.isNew && sidepanelState === 'open'
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      setOpenSurface({ id: MINI_MENU_SURFACE_ID, open: nextOpen })
      if (nextOpen) {
        setMiniHovered(true)
        setContentHovered(true)
      } else if (!document.querySelector('[data-mini-notch-shell]')?.matches(':hover')) {
        // The open held the hover, and a close off the notch fires no leave; the notch's toast timers wait on it
        setContentHovered(false)
      }
    },
    [setOpenSurface, setMiniHovered, setContentHovered],
  )

  useEffect(() => {
    return () => {
      setOpenSurface({ id: MINI_MENU_SURFACE_ID, open: false })
    }
  }, [setOpenSurface])

  const trackMenuItem = useCallback(
    (item: 'new chat' | 'view meeting' | 'new meeting' | 'settings') =>
      trackNotchAction('menu item', { item, notched: isNotched }),
    [isNotched],
  )

  const handleNewChat = useCallback(() => {
    if (isAlreadyInNewChat) return
    trackMenuItem('new chat')
    openViewInSidepanel('chat', { isNew: true })
  }, [isAlreadyInNewChat, openViewInSidepanel, trackMenuItem])

  const handleViewMeeting = useCallback(() => {
    trackMenuItem('view meeting')
    openViewInSidepanel(MEETING_VIEW_ID, liveMeeting)
  }, [liveMeeting, openViewInSidepanel, trackMenuItem])

  const handleMenuNewMeeting = useCallback(() => {
    trackMenuItem('new meeting')
    handleNewMeeting()
  }, [handleNewMeeting, trackMenuItem])

  const handleSettings = useCallback(() => {
    trackMenuItem('settings')
    WindowIpcRenderer.openSettingsWindow()
  }, [trackMenuItem])

  const handleDisableIncognito = useCallback(async () => {
    try {
      await disableIncognito('mini-menu')
    } catch (error) {
      console.error('Failed to disable incognito mode:', error)
    }
  }, [disableIncognito])

  const handleIncognitoForOneHour = useCallback(async () => {
    try {
      await enableIncognito(1 * 60 * 60 * 1000, 'mini-menu')
    } catch (error) {
      console.error('Failed to enable incognito mode:', error)
    }
  }, [enableIncognito])

  const handleIncognitoUntilMorning = useCallback(async () => {
    try {
      await enableIncognito(durationUntilMorning(), 'mini-menu')
    } catch (error) {
      console.error('Failed to enable incognito mode:', error)
    }
  }, [enableIncognito])

  return (
    <Menu open={open} onOpenChange={handleOpenChange}>
      <div onClick={(e) => e.stopPropagation()}>
        <Menu.Trigger
          variant="ghost"
          size={isNotched ? 'md' : 'sm'}
          shape={isNotched ? 'square' : undefined}
          className={twMerge(
            // The pill's far end. The inner side keeps the button's own squircle corner; the outer
            // side is a plain round corner of half the button's height, so it stays concentric with
            // the pill whichever edge the Mini is docked to. Half the height, not `rounded-full`: an
            // oversized radius makes the browser scale every corner down, the inner ones included.
            !isNotched && 'px-1.5',
            !isNotched &&
              (isMiniOnLeftSide
                ? 'rounded-r-[calc(var(--btn-h)/2)] [corner-bottom-right-shape:round] [corner-top-right-shape:round]'
                : 'rounded-l-[calc(var(--btn-h)/2)] [corner-bottom-left-shape:round] [corner-top-left-shape:round]'),
          )}
        >
          <ChevronDown strokeWidth={3} className="text-foreground-secondary" />
        </Menu.Trigger>
      </div>
      <Menu.Content
        side="bottom"
        align={isMiniOnLeftSide ? 'end' : 'start'}
        sideOffset={12}
        positionerClassName="z-30 after:absolute after:-inset-2 after:-z-10 after:content-['']"
      >
        {/* Notched mini starts chats and meetings from its own bar */}
        {isLoggedIn && isNotched && hasActiveMeeting && (
          <>
            <Menu.Item onClick={handleViewMeeting}>View Meeting</Menu.Item>
            <Menu.Separator />
          </>
        )}
        {isLoggedIn && !isNotched && (
          <>
            <Menu.Item onClick={handleNewChat}>New Chat</Menu.Item>
            <Tooltip disabled={!isIncognitoEnabled}>
              <Tooltip.Trigger render={<div className="relative flex" />}>
                <Menu.Item
                  className="flex-1"
                  onClick={hasActiveMeeting ? handleViewMeeting : handleMenuNewMeeting}
                  disabled={isIncognitoEnabled}
                >
                  {hasActiveMeeting ? 'View Meeting' : 'New Meeting'}
                </Menu.Item>
              </Tooltip.Trigger>
              <Tooltip.Content side="right" className="whitespace-pre-line text-center">
                {'Turn off incognito mode\nto start transcribing'}
              </Tooltip.Content>
            </Tooltip>
            <Menu.Separator />
          </>
        )}
        <Menu.SubmenuRoot>
          <Tooltip disabled={!(hasActiveMeeting && !isIncognitoEnabled)}>
            <Tooltip.Trigger render={<div className="relative flex" />}>
              <Menu.SubmenuTrigger className="flex-1" disabled={hasActiveMeeting && !isIncognitoEnabled}>
                <HatGlasses className={twMerge('size-4', !isIncognitoEnabled && 'opacity-70')} />
                <span className="flex-1">Incognito Mode</span>
                {isIncognitoEnabled && (
                  <Badge size="sm" tone="success">
                    On
                  </Badge>
                )}
              </Menu.SubmenuTrigger>
            </Tooltip.Trigger>
            <Tooltip.Content side="right">End your {noteNoun(hasMeeting)} first</Tooltip.Content>
          </Tooltip>
          <Menu.Content>
            {isIncognitoEnabled ? (
              <Menu.Item onClick={handleDisableIncognito}>
                <span>Turn off</span>
                {incognitoTimer && (
                  <span className="text-foreground-placeholder ml-auto text-xs">
                    <TimeAgo
                      date={incognitoTimer}
                      formatter={incognitoTimeRemainingCompactFormatter}
                      minPeriod={60}
                      live={true}
                    />
                  </span>
                )}
              </Menu.Item>
            ) : (
              <>
                <Menu.Group>
                  <Menu.GroupLabel className="max-w-48 whitespace-normal text-xs">
                    {INCOGNITO_EXPLAINER}
                  </Menu.GroupLabel>
                </Menu.Group>
                <Menu.Separator />
                <Menu.Item onClick={handleIncognitoForOneHour}>
                  <span>For 1 hour</span>
                </Menu.Item>
                <Menu.Item onClick={handleIncognitoUntilMorning}>
                  <span>Until morning</span>
                </Menu.Item>
              </>
            )}
          </Menu.Content>
        </Menu.SubmenuRoot>
        <Menu.Item onClick={handleSettings}>Settings</Menu.Item>
      </Menu.Content>
    </Menu>
  )
}

interface MiniMenuProps {
  forceShow?: boolean
}

export function MiniMenu({ forceShow = false }: MiniMenuProps) {
  const isContentHovered = useIsMiniContentHovered()
  const isMiniHovered = useIsMiniHovered()
  const isMenuOpen = useIsMiniMenuOpen()
  const showMenu = forceShow || isMenuOpen || (isContentHovered && isMiniHovered)
  const isAnchoredLeft = useIsMiniAnchoredLeft()

  return (
    <AnimatePresence initial={false} mode="popLayout" anchorX={miniPresenceAnchor(isAnchoredLeft)}>
      {showMenu && (
        <motion.div
          key="mini-menu"
          variants={miniRevealVariants(isAnchoredLeft)}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={MINI_REVEAL_TRANSITION}
        >
          <MiniMenuButton />
        </motion.div>
      )}
    </AnimatePresence>
  )
}
